const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL || "http://localhost:5100/api/v2";

import { encryptData, decryptData } from "../utils/encryption";
import { getClientIp } from "../utils/client-ip";
import logger from "@/utils/logger";

export class ApiError extends Error {
  status?: number;
  data?: any;

  constructor(message: string, status?: number, data?: any) {
    super(message);
    this.status = status;
    this.data = data;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  private async request(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<any> {
    let url = `${this.baseURL}${endpoint}`;
    const isFormData = options.body instanceof FormData;

    // For GET requests, send the device IP on the query string by
    // default. A small allowlist of GET endpoints also gets the full
    // encrypted envelope (`?data=<encrypted>`) — this mirrors the POST
    // body shape (`{ data: <encrypted> }`) and keeps `ip_addr` inside
    // the decrypted payload on the server. The IP lookup is cached in
    // client-ip.ts (effectively free after the first call).
    if ((options.method || "GET") === "GET") {
      // GET endpoints that require the encrypted envelope
      const getEncryptionEndpoints = [
        "/user/assessment/getDetails/",
      ];
      const shouldEncryptGet = getEncryptionEndpoints.some(ep => endpoint.includes(ep));

      if (shouldEncryptGet) {
        try {
          const ip = await getClientIp();
          const queryStart = url.indexOf("?");
          const baseUrl = queryStart >= 0 ? url.substring(0, queryStart) : url;
          const existingQuery = queryStart >= 0 ? url.substring(queryStart + 1) : "";

          // Build the payload to encrypt: any pre-existing query params + ip_addr
          const payload: Record<string, string> = {};
          if (existingQuery) {
            existingQuery.split("&").forEach((pair) => {
              if (!pair) return;
              const eqIdx = pair.indexOf("=");
              const key = eqIdx >= 0 ? pair.substring(0, eqIdx) : pair;
              const value = eqIdx >= 0 ? pair.substring(eqIdx + 1) : "";
              if (key && !(key in payload)) {
                payload[key] = decodeURIComponent(value);
              }
            });
          }
          if (ip) {
            payload.ip_addr = ip;
          }

          const encryptedPayload = encryptData(payload);
          url = `${baseUrl}?data=${encodeURIComponent(encryptedPayload)}`;
        } catch (e) {
          logger.error("GET encryption failed:", e);
        }
      }
    }

    // Skip encryption for these endpoints (they handle their own encryption or skip it)
    const skipEncryptionEndpoints = [
      "/user/course/assessment/run-code",
      "/user/course/assessment/submit-code",
      "/assessment/run-code",
      "/assessment/submit-code",
      "/markQuestion",
      "/unMarkQuestion",
      "/writingAutoSave",
      "/run_code",
      "/compiler_languages",
    ];
    const shouldSkipEncryption = skipEncryptionEndpoints.some(ep => endpoint.includes(ep));

    // Encrypt payload for POST requests if it's a JSON request (not FormData) and not skipped
    if (!isFormData && options.method === 'POST' && options.body && !shouldSkipEncryption) {
      try {
        logger.debug(`[API] Encrypting POST request to ${endpoint}...`);
        const originalBody = JSON.parse(options.body as string);
        // Inject the device IP so every POST carries it on `ip_addr`.
        // The lookup is cached in client-ip.ts so this is effectively free
        // after the first call. If the lookup fails (offline, blocked),
        // we simply skip the field and continue.
        const ip = await getClientIp();
        if (ip && typeof originalBody === "object" && originalBody !== null) {
          originalBody.ip_addr = ip;
        }
        logger.debug(`[API] Original body before encryption:`, originalBody);
        const encryptedPayload = encryptData(originalBody);
        logger.debug(`[API] Encrypted payload:`, encryptedPayload);
        options.body = JSON.stringify({ data: encryptedPayload });
      } catch (e) {
        logger.error("Encryption failed:", e);
      }
    } else if (!isFormData && options.method === 'POST' && options.body) {
      // Same IP injection for POSTs that skip server-side encryption
      // (the run-code / submit-code endpoints). They pass through as
      // plain JSON, so we mutate the body string before fetch.
      try {
        const ip = await getClientIp();
        if (ip) {
          const originalBody = JSON.parse(options.body as string);
          if (originalBody && typeof originalBody === "object") {
            originalBody.ip_addr = ip;
            options.body = JSON.stringify(originalBody);
          }
        }
      } catch (e) {
        logger.error("IP injection failed:", e);
      }
    } else if (isFormData && options.method === 'POST' && (options.body as FormData) && !shouldSkipEncryption) {
      // For FormData POSTs (file uploads etc.), append the IP as a field
      try {
        const ip = await getClientIp();
        if (ip) {
          (options.body as FormData).append("ip_addr", ip);
        }
      } catch (e) {
        logger.error("IP injection (FormData) failed:", e);
      }
    }

   
    const controller = new AbortController();


    const defaultHeaders: Record<string, string> = {};

    // Add token if not public
    const publicEndpoints = [
      "/superadmin/login",
      "/admin/login",
      "/login",
      "/login/shenron",
      "/logout",
      "/signup",
      "/forgot-password",
      "/reset-password",
      "/verify-email",
      "/vendor/login",
      "/vendorteam/login",
      "/vendorteam/verify-login-otp",
      "/institution/login",
      "/faculty/login",
      "/support/login",
      "/trainer/login",
    ];

    const token =
      typeof window !== "undefined" ? localStorage.getItem("token") : null;

    if (token && !publicEndpoints.includes(endpoint)) {
      defaultHeaders["Authorization"] = `Bearer ${token}`;
    }

    // Setup headers (skip setting Content-Type for FormData)
    const headers = isFormData
      ? { ...defaultHeaders, ...options.headers }
      : {
        "Content-Type": "application/json",
        ...defaultHeaders,
        ...options.headers,
      };

    const config: RequestInit = {
      ...options,
      headers,
      signal: controller.signal,
    };

    try {
      
      const response = await fetch(url, config);
      

      if (response.status === 401 || response.status === 403) {
        const errorText = await response.text();
        let errorMessage = "";
        let errorData: any = null;

        try {
          errorData = JSON.parse(errorText);
          errorMessage = errorData.message || "";
        } catch {
          errorMessage = errorText;
        }

        // For login endpoints, NEVER redirect — just show the error on the login page
        if (publicEndpoints.includes(endpoint)) {
          throw new ApiError(errorMessage || "Authentication failed", response.status, errorData?.data);
        }

        // For non-login endpoints: only redirect on clear auth failures
        if (
          response.status === 401 ||
          (response.status === 403 &&
            errorMessage.toLowerCase().includes("token"))
        ) {
          if (typeof window !== "undefined") {
            if (!window.location.pathname.includes("/user/exam-preview")) {
              const authKeys = [
                "token",
                "isAuthenticated",
                "facultyAuthenticated",
                "institutionAuthenticated",
                "superadminAuthenticated",
                "userAuthenticated",
                "vendorAuthenticated",
                "trainerAuthenticated",
                "userEmail",
                "userRole",
                "userName",
                "userId"
              ];
              authKeys.forEach(key => localStorage.removeItem(key));

              // Redirect to the correct login page based on the current portal
              const path = window.location.pathname;
              if (!path.endsWith("/login") && !path.startsWith("/attendance")) {
                if (path.startsWith("/trainer")) {
                  window.location.href = "/trainer/login";
                } else if (path.startsWith("/admin")) {
                  window.location.href = "/admin/login";
                } else if (path.startsWith("/institution")) {
                  window.location.href = "/institution/login";
                } else if (path.startsWith("/vendor")) {
                  window.location.href = "/vendor/login";
                } else if (path.startsWith("/faculty")) {
                  window.location.href = "/faculty/login";
                } else if (path.startsWith("/superadmin")) {
                  window.location.href = "/superadmin/login";
                } else if (path === "/team" || path.startsWith("/team/")) {
                  window.location.href = "/team/login";
                } else {
                  window.location.href = "/";
                }
              }
            }
          }
          throw new ApiError("Authentication failed", response.status, errorData?.data);
        } else {
          throw new ApiError(errorMessage || "Access denied", response.status, errorData?.data);
        }
      }

      if (!response.ok) {
        const errorText = await response.text();
        if (response.status === 404) {
          logger.warn(`API 404 (${response.status}):`, errorText);
        } else {
          logger.error(`API Error Response (${response.status}):`, errorText);
        }

        // Try to parse the error response as JSON to extract the message
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.data?.show_report === false || errorData.data?.show_reports === 0) {
            throw new ApiError("you do not have access to view this result", response.status, errorData.data);
          }
          if (errorData.error) {
            throw new ApiError(errorData.error, response.status, errorData.data);
          } else if (errorData.message) {
            throw new ApiError(errorData.message, response.status, errorData.data);
          }
          throw new ApiError(`HTTP error! status: ${response.status}`, response.status);
        } catch (parseError) {
          // If the parseError is actually our thrown error with the message, re-throw it
          if (
            parseError instanceof ApiError || (
              parseError instanceof Error &&
              !parseError.message.includes("Unexpected")
            )
          ) {
            throw parseError;
          }
          // If parsing truly fails, use generic error
          logger.error("Failed to parse error response:", parseError);
          throw new ApiError(`HTTP error! status: ${response.status}`, response.status);
        }
      }

      const data = await response.json();

      // Throw error if backend returns success: 0
      if (data.success === 0 && data.message) {
        throw new Error(data.message);
      }

      // Automatically decrypt response if it's in the standard encrypted envelope
      if (data.data && typeof data.data === 'string' && (data.data.includes(':') || data.data.length > 32)) {
        // Attempt to decrypt
        try {
          logger.debug(`[API] Decrypting response from ${endpoint}...`);
          const decrypted = decryptData(data.data);
          if (decrypted) {
            data.data = decrypted;
          } else {
            logger.error(`[API] Decryption returned null for ${endpoint}. Data preview: ${data.data.substring(0, 50)}...`);
          }
        } catch (e) {
          logger.error(`[API] Decryption error for ${endpoint}:`, e);
          // Fall through, maybe it wasn't actually encrypted or decryption failed
        }
      }

      return data;
    } catch (error) {
 
      if (error instanceof Error && error.name === "AbortError") {
        logger.error("Request timeout:", endpoint);
        
      }

      // Handle network errors (Failed to fetch)
      if (error instanceof TypeError && error.message === "Failed to fetch") {
        logger.error("Network error - Backend may be down:", url);
        throw new Error(
          "We're having trouble connecting to our servers. Please check your network connection or try again in a few moments.",
        );
      }

      logger.error("API request failed:", error);
      throw error;
    }
  }

  private async requestBlob(
    endpoint: string,
    options: RequestInit = {},
    timeoutMs?: number,
  ): Promise<Blob> {
    let url = `${this.baseURL}${endpoint}`;

    // For GET (Blob) requests, send the device IP on the query string
    // by default. A small allowlist of GET endpoints also gets the full
    // encrypted envelope — same allowlist and shape as JSON GETs.
    if ((options.method || "GET") === "GET") {
      const getEncryptionEndpoints = [
        "/user/assessment/getDetails/",
      ];
      const shouldEncryptGet = getEncryptionEndpoints.some(ep => endpoint.includes(ep));

      if (shouldEncryptGet) {
        try {
          const ip = await getClientIp();
          const queryStart = url.indexOf("?");
          const baseUrl = queryStart >= 0 ? url.substring(0, queryStart) : url;
          const existingQuery = queryStart >= 0 ? url.substring(queryStart + 1) : "";

          const payload: Record<string, string> = {};
          if (existingQuery) {
            existingQuery.split("&").forEach((pair) => {
              if (!pair) return;
              const eqIdx = pair.indexOf("=");
              const key = eqIdx >= 0 ? pair.substring(0, eqIdx) : pair;
              const value = eqIdx >= 0 ? pair.substring(eqIdx + 1) : "";
              if (key && !(key in payload)) {
                payload[key] = decodeURIComponent(value);
              }
            });
          }
          if (ip) {
            payload.ip_addr = ip;
          }

          const encryptedPayload = encryptData(payload);
          url = `${baseUrl}?data=${encodeURIComponent(encryptedPayload)}`;
        } catch (e) {
          logger.error("GET (Blob) encryption failed:", e);
        }
      } else {
        try {
          const ip = await getClientIp();
          if (ip) {
            const separator = url.includes("?") ? "&" : "?";
            url = `${url}${separator}ip_addr=${encodeURIComponent(ip)}`;
          }
        } catch (e) {
          logger.error("IP injection failed:", e);
        }
      }
    }

    const isFormData = options.body instanceof FormData;

    // Encrypt payload for POST requests if it's a JSON request (not FormData)
    if (!isFormData && options.method === 'POST' && options.body) {
      try {
        logger.debug(`[API] Encrypting POST (Blob) request to ${endpoint}...`);
        const originalBody = JSON.parse(options.body as string);
        // Inject the device IP into every POST body as `ip_addr`.
        const ip = await getClientIp();
        if (ip && originalBody && typeof originalBody === "object") {
          originalBody.ip_addr = ip;
        }
        const encryptedPayload = encryptData(originalBody);
        options.body = JSON.stringify({ data: encryptedPayload });
      } catch (e) {
        logger.error("Encryption failed:", e);
      }
    } else if (isFormData && options.method === 'POST' && options.body) {
      try {
        const ip = await getClientIp();
        if (ip) {
          (options.body as FormData).append("ip_addr", ip);
        }
      } catch (e) {
        logger.error("IP injection (FormData) failed:", e);
      }
    }

    // Blob requests get longer timeout (2 min default, or custom)
    const blobTimeout = timeoutMs || 120000;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), blobTimeout);

    const defaultHeaders: Record<string, string> = {};

    // Add token if not public
    const publicEndpoints = [
      "/superadmin/login",
      "/admin/login",
      "/login",
      "/login/shenron",
      "/logout",
      "/signup",
      "/forgot-password",
      "/reset-password",
      "/verify-email",
      "/vendor/login",
      "/vendorteam/login",
      "/vendorteam/verify-login-otp",
      "/institution/login",
      "/faculty/login",
      "/support/login",
      "/trainer/login",
    ];

    const token =
      typeof window !== "undefined" ? localStorage.getItem("token") : null;

    if (token && !publicEndpoints.includes(endpoint)) {
      defaultHeaders["Authorization"] = `Bearer ${token}`;
    }

    // Setup headers (skip setting Content-Type for FormData)
    const headers = isFormData
      ? { ...defaultHeaders, ...options.headers }
      : {
        "Content-Type": "application/json",
        ...defaultHeaders,
        ...options.headers,
      };

    const config: RequestInit = {
      ...options,
      headers,
      signal: controller.signal,
    };

    try {
      const response = await fetch(url, config);

      if (response.status === 401 || response.status === 403) {
        const errorText = await response.text();
        let errorMessage = "";

        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || "";
        } catch {
          errorMessage = errorText;
        }

        // For login endpoints, NEVER redirect — just show the error on the login page
        if (publicEndpoints.includes(endpoint)) {
          throw new Error(errorMessage || "Authentication failed");
        }

        // For non-login endpoints: only redirect on clear auth failures
        if (
          response.status === 401 ||
          (response.status === 403 &&
            errorMessage.toLowerCase().includes("token"))
        ) {
          if (typeof window !== "undefined") {
            const authKeys = [
              "token",
              "isAuthenticated",
              "facultyAuthenticated",
              "institutionAuthenticated",
              "superadminAuthenticated",
              "userAuthenticated",
              "vendorAuthenticated",
              "trainerAuthenticated",
              "userEmail",
              "userRole",
              "userName",
              "userId"
            ];
            authKeys.forEach(key => localStorage.removeItem(key));

            // Redirect to the correct login page based on the current portal
            const path = window.location.pathname;
            if (!path.endsWith("/login") && !path.startsWith("/attendance")) {
              if (path.startsWith("/trainer")) {
                window.location.href = "/trainer/login";
              } else if (path.startsWith("/admin")) {
                window.location.href = "/admin/login";
              } else if (path.startsWith("/institution")) {
                window.location.href = "/institution/login";
              } else if (path.startsWith("/vendor")) {
                window.location.href = "/vendor/login";
              } else if (path.startsWith("/faculty")) {
                window.location.href = "/faculty/login";
              } else if (path.startsWith("/superadmin")) {
                window.location.href = "/superadmin/login";
              } else if (path === "/team" || path.startsWith("/team/")) {
                window.location.href = "/team/login";
              } else {
                window.location.href = "/";
              }
            }
          }
          throw new Error("Authentication failed");
        } else {
          throw new Error(errorMessage || "Access denied");
        }
      }

      if (!response.ok) {
        const errorText = await response.text();

        // Try to parse the error response as JSON to extract the message
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.data?.show_report === false || errorData.data?.show_reports === 0) {
            throw new Error("you do not have access to view this result");
          }
          if (errorData.error) {
            throw new Error(errorData.error);
          } else if (errorData.message) {
            throw new Error(errorData.message);
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        } catch (parseError) {
          // If the parseError is actually our thrown error with the message, re-throw it
          if (
            parseError instanceof Error &&
            !parseError.message.includes("Unexpected")
          ) {
            throw parseError;
          }
          // If parsing truly fails, use generic error
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }

      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const errorText = await response.text();
        try {
          const errorData = JSON.parse(errorText);
          const message = errorData.message || errorData.error || "Unknown error occurred";
          throw new Error(message);
        } catch (e) {
          throw new Error("Server returned an invalid JSON response instead of a file.");
        }
      }

      const blob = await response.blob();
      if (blob.size === 0) {
        throw new Error("Received empty file from server");
      }
      return blob;
    } catch (error) {
      // Handle network errors (Failed to fetch)
      if (error instanceof TypeError && error.message === "Failed to fetch") {
        logger.error("Network error - Backend may be down:", url);
        throw new Error(
          "We're having trouble connecting to our servers. Please check your network connection or try again in a few moments.",
        );
      }

      logger.error("API request failed:", error);
      throw error;
    }
  }

  async get(endpoint: string): Promise<any> {
    return this.request(endpoint, { method: "GET" });
  }

  async post(endpoint: string, data?: any): Promise<any> {
    // Debug logging for assessment creation
    if (endpoint === "/assessment/create" && data?.basic?.exam_category_id) {
      logger.debug(
        "API Client: Sending exam_category_id:",
        data.basic.exam_category_id,
        "Type:",
        typeof data.basic.exam_category_id,
      );
    }

    // Debug logging for course access
    if (endpoint.includes("course/access/add")) {
      logger.debug("[API Client] POST course/access/add payload:", JSON.stringify(data, null, 2));
      logger.debug("[API Client] Type of batches:", typeof data?.batches, Array.isArray(data?.batches));
    }

    // Ensure data is an object, not undefined (for encryption to work)
    const payload = (data === undefined || data === null) ? {} : data;

    return this.request(endpoint, {
      method: "POST",
      body: payload instanceof FormData ? payload : JSON.stringify(payload),
    });
  }

  /**
   * Starts an authenticated, encrypted POST that may continue while the page
   * is being unloaded. Injects `ip_addr` into the encrypted body to mirror
   * the regular POST path; `getClientIp()` is cached at module level so
   * this is effectively free after the first call (no network round-trip
   * during the unload window). If the lookup hasn't resolved yet or has
   * failed, we still dispatch — the body just omits the field.
   */
  async postKeepalive(endpoint: string, data?: any): Promise<any> {
    const payload: Record<string, any> =
      data === undefined || data === null
        ? {}
        : typeof data === "object" && !Array.isArray(data)
          ? { ...data }
          : { value: data };
    try {
      const ip = await getClientIp();
      if (ip && typeof payload === "object" && payload !== null) {
        payload.ip_addr = ip;
      }
    } catch (e) {
      logger.error("Keepalive IP injection failed:", e);
    }
    const encryptedPayload = encryptData(payload);
    const token =
      typeof window !== "undefined" ? localStorage.getItem("token") : null;
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: "POST",
      headers,
      body: JSON.stringify({ data: encryptedPayload }),
      keepalive: true,
    });

    if (!response.ok) {
      throw new ApiError(
        `Keepalive request failed with status ${response.status}`,
        response.status,
      );
    }

    try {
      const body = await response.json();
      if (
        body?.data &&
        typeof body.data === "string" &&
        (body.data.includes(":") || body.data.length > 32)
      ) {
        const decrypted = decryptData(body.data);
        if (decrypted) body.data = decrypted;
      }
      return body;
    } catch (e) {
      logger.error("Keepalive response parse failed:", e);
      return null;
    }
  }

  async put(endpoint: string, data?: any): Promise<any> {
    return this.request(endpoint, {
      method: "PUT",
      body: data instanceof FormData ? data : JSON.stringify(data),
    });
  }

  async patch(endpoint: string, data?: any): Promise<any> {
    return this.request(endpoint, {
      method: "PATCH",
      body: data instanceof FormData ? data : JSON.stringify(data),
    });
  }

  async delete(endpoint: string, data?: any): Promise<any> {
    return this.request(endpoint, {
      method: "DELETE",
      body:
        data instanceof FormData
          ? data
          : data
            ? JSON.stringify(data)
            : undefined,
    });
  }

  async postBlob(endpoint: string, data?: any, timeoutMs?: number): Promise<Blob> {
    return this.requestBlob(endpoint, {
      method: "POST",
      body: data instanceof FormData ? data : JSON.stringify(data),
    }, timeoutMs);
  }

  async getBlob(endpoint: string, timeoutMs?: number): Promise<Blob> {
    return this.requestBlob(endpoint, {
      method: "GET",
    }, timeoutMs);
  }

  /**
   * Open a system-wide authenticated WebSocket to this API.
   *
   * Browser WebSockets cannot set custom headers, so the JWT is passed in the
   * query string — the server-side upgrade handler in `ws/socket.js` reads
   * `?token=…` (with `Authorization: Bearer` as a fallback for proxies that
   * do pass headers). If `token` is omitted the token is read from
   * localStorage, matching the pattern used by every other request in this
   * client.
   *
   * @param path  path relative to baseURL (e.g. "/ws/compiler"). baseURL
   *              already includes the `/api/v2` prefix.
   * @param opts.token  override the auto-picked token (e.g. for tests).
   * @param opts.protocols  optional subprotocols list passed to the WebSocket.
   */
  connectWebSocket(
    path: string,
    opts: { token?: string | null; protocols?: string | string[] } = {},
  ): WebSocket {
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    const wsBase = this.baseURL.replace(/^http/i, "ws").replace(/\/+$/, "");
    const token =
      opts.token !== undefined
        ? opts.token
        : this.getToken();

    const sep = cleanPath.includes("?") ? "&" : "?";
    const url = token
      ? `${wsBase}${cleanPath}${sep}token=${encodeURIComponent(token)}`
      : `${wsBase}${cleanPath}`;

    return new WebSocket(url, opts.protocols);
  }

  /**
   * Read the JWT from localStorage, normalized to a bare token string.
   *
   * Some auth flows in this app store the value as a JSON-stringified
   * "Bearer xxx" string (i.e. with surrounding quotes and a `Bearer ` prefix
   * baked in). Both the HTTP `Authorization` header and the WS `?token=`
   * query expect a bare token, so this helper strips:
   *   - surrounding double quotes (JSON.stringify artifact)
   *   - a leading `Bearer ` if the storage value already includes it
   *   - surrounding whitespace
   */
  getToken(): string | null {
    if (typeof window === "undefined") return null;
    const raw = localStorage.getItem("token");
    if (!raw) return null;
    const trimmed = raw.trim();
    if (!trimmed) return null;
    // Strip surrounding double quotes from a JSON-stringified value.
    const unquoted =
      trimmed.length >= 2 &&
      trimmed.startsWith('"') &&
      trimmed.endsWith('"')
        ? trimmed.slice(1, -1)
        : trimmed;
    // Strip a leading "Bearer " if the value already includes the scheme.
    return unquoted.replace(/^Bearer\s+/i, "");
  }

  /**
   * Send a POST request and consume the response as a Server-Sent Events (SSE / text/event-stream) stream
   */
  async postStream(
    endpoint: string,
    data: any,
    callbacks: {
      onEvent?: (event: { event: string; data: any }) => void;
      onError?: (error: any) => void;
      onEnd?: () => void;
    },
    options: {
      signal?: AbortSignal;
      headers?: Record<string, string>;
    } = {}
  ): Promise<void> {
    const url = `${this.baseURL}${endpoint}`;
    const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

    const defaultHeaders: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    };

    if (token) {
      defaultHeaders["Authorization"] = `Bearer ${token}`;
    }

    const payload = { ...(data || {}) };
    try {
      const ip = await getClientIp();
      if (ip) {
        payload.ip_addr = ip;
      }
    } catch (e) {
      logger.error("IP injection failed:", e);
    }

    let requestBody: string;
    try {
      logger.debug(`[API] Encrypting stream POST request to ${endpoint}...`);
      const encryptedPayload = encryptData(payload);
      requestBody = JSON.stringify({ data: encryptedPayload });
    } catch (e) {
      logger.error("Stream encryption failed, falling back to raw payload:", e);
      requestBody = JSON.stringify(payload);
    }

    const response = await fetch(url, {
      method: "POST",
      headers: {
        ...defaultHeaders,
        ...(options.headers || {}),
      },
      body: requestBody,
      signal: options.signal,
    });

    if (!response.ok) {
      let errorMessage = `Request failed with status ${response.status}`;
      try {
        const errJson = await response.json();
        errorMessage = errJson.message || errorMessage;
      } catch {
        const text = await response.text();
        if (text) errorMessage = text;
      }
      const apiErr = new ApiError(errorMessage, response.status);
      callbacks.onError?.(apiErr);
      throw apiErr;
    }

    if (!response.body) {
      const err = new Error("Response body is not readable");
      callbacks.onError?.(err);
      throw err;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split(/\r?\n\r?\n/);
        buffer = parts.pop() ?? "";

        for (const block of parts) {
          const trimmed = block.trim();
          if (!trimmed) continue;

          let eventType = "message";
          const dataLines: string[] = [];

          for (const line of trimmed.split(/\r?\n/)) {
            if (line.startsWith("event:")) {
              eventType = line.replace(/^event:\s*/, "").trim();
            } else if (line.startsWith("data:")) {
              dataLines.push(line.replace(/^data:\s*/, ""));
            }
          }

          if (dataLines.length > 0) {
            const rawData = dataLines.join("\n");
            let parsedData: any = rawData;
            try {
              parsedData = JSON.parse(rawData);
            } catch {
              parsedData = rawData;
            }

            callbacks.onEvent?.({
              event: eventType,
              data: parsedData,
            });
          }
        }
      }

      if (buffer.trim()) {
        const trimmed = buffer.trim();
        let eventType = "message";
        const dataLines: string[] = [];
        for (const line of trimmed.split(/\r?\n/)) {
          if (line.startsWith("event:")) {
            eventType = line.replace(/^event:\s*/, "").trim();
          } else if (line.startsWith("data:")) {
            dataLines.push(line.replace(/^data:\s*/, ""));
          }
        }
        if (dataLines.length > 0) {
          const rawData = dataLines.join("\n");
          let parsedData: any = rawData;
          try {
            parsedData = JSON.parse(rawData);
          } catch {
            parsedData = rawData;
          }
          callbacks.onEvent?.({
            event: eventType,
            data: parsedData,
          });
        }
      }

      callbacks.onEnd?.();
    } catch (err: any) {
      if (err.name === "AbortError") {
        logger.debug("[API] Stream aborted by caller");
        return;
      }
      callbacks.onError?.(err);
      throw err;
    } finally {
      reader.releaseLock();
    }
  }
}

export const apiClient = new ApiClient(API_BASE_URL);